// store.ts — typed front-end wrappers over the on-device Rust SQLite commands.
// Every call hits LOCAL SQLite via Tauri IPC (durable, offline, on-disk). Nothing
// here ever talks to a server; the three AI actions (Digest / Explain / Plan) are in
// brain.ts and send only the text the user explicitly submits.
//
// Data model:
//   subject        { id, name, + computed counts }
//   material       { id, subject_id, topic, title, source 'typed'|'photo', content }
//   artifact       { id, subject_id, material_id, kind 'notes'|'explanation'|'plan',
//                    title, body (JSON string — StudyNotes | Explanation | StudyPlan) }
//   study session  { id, subject_id, studied_on, minutes, note }

import { invoke } from "./tauri-web";

export type MaterialSource = "typed" | "photo" | "lecture";
export type ArtifactKind = "notes" | "explanation" | "plan";

export interface Subject {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
  material_count: number;
  artifact_count: number;
  session_count: number;
  total_minutes: number;
  last_studied: string | null;
}

export interface MaterialPhoto {
  id: string;
  material_id: string;
  /** Absolute on-device path. Render with convertFileSrc(); never uploaded. */
  path: string;
  created_at: string;
}

export interface Material {
  id: string;
  subject_id: string;
  /** Topic tag, normalized lowercase. "" when not set. */
  topic: string;
  title: string;
  source: MaterialSource;
  /** The study text. Stays on-device; sent ONLY when the user runs Digest on it. */
  content: string;
  created_at: string;
  updated_at: string;
}

export interface NewMaterial {
  subject_id: string;
  topic?: string;
  title: string;
  source?: MaterialSource;
  content: string;
}

export interface Artifact {
  id: string;
  subject_id: string;
  /** The material this was generated from ("" for explain/plan artifacts). */
  material_id: string;
  kind: ArtifactKind;
  title: string;
  /** JSON string of the structured artifact — parse with parseArtifactBody(). */
  body: string;
  created_at: string;
}

export interface NewArtifact {
  subject_id: string;
  material_id?: string;
  kind: ArtifactKind;
  title: string;
  body: string;
}

export interface StudySession {
  id: string;
  subject_id: string;
  studied_on: string;
  minutes: number;
  note: string;
  created_at: string;
}

export interface NewSession {
  subject_id: string;
  studied_on?: string;
  minutes: number;
  note?: string;
}

// ── Structured artifact bodies (what the brain returns; stored as JSON strings) ──

/** Digest output — structured study notes over the user's OWN material. */
export interface StudyNotes {
  key_concepts: { term: string; definition: string }[];
  summary: string;
  /** Self-check questions whose answers are EXPLANATIONS (teach, don't hand answers). */
  self_check: { question: string; explanation: string }[];
  /** Key-concept terms the student marked. Persisted back into the artifact body. */
  highlights?: string[];
}

/** Explain output — a layered explanation (simple → precise → applied → pitfall). */
export interface Explanation {
  concept: string;
  analogy: string;
  definition: string;
  worked_example: string;
  misconception: string;
}

export interface PlanWeek {
  week: number;
  label: string;
  focus: string[];
  milestone: string;
  review: string;
}

/** Plan output — a week-by-week learning plan with milestones + review points. */
export interface StudyPlan {
  goal: string;
  weeks: PlanWeek[];
  note: string;
}

export type ArtifactBody =
  | { kind: "notes"; notes: StudyNotes }
  | { kind: "explanation"; explanation: Explanation }
  | { kind: "plan"; plan: StudyPlan };

/** Parse an artifact's stored JSON body by kind. Returns null when it can't be read
 *  (never throws — a corrupt row must not take the library screen down). */
export function parseArtifactBody(a: Artifact): ArtifactBody | null {
  try {
    const parsed = JSON.parse(a.body);
    if (a.kind === "notes") return { kind: "notes", notes: parsed as StudyNotes };
    if (a.kind === "explanation") return { kind: "explanation", explanation: parsed as Explanation };
    if (a.kind === "plan") return { kind: "plan", plan: parsed as StudyPlan };
    return null;
  } catch {
    return null;
  }
}

export const store = {
  // Subjects
  listSubjects: () => invoke<Subject[]>("list_subjects"),
  createSubject: (name: string) => invoke<Subject>("create_subject", { name }),
  renameSubject: (id: string, name: string) => invoke<void>("rename_subject", { id, name }),
  deleteSubject: (id: string) => invoke<void>("delete_subject", { id }),
  // Materials
  listMaterials: (subjectId?: string) => invoke<Material[]>("list_materials", { subjectId: subjectId ?? null }),
  getMaterial: (id: string) => invoke<Material | null>("get_material", { id }),
  createMaterial: (input: NewMaterial) => invoke<Material>("create_material", { input }),
  deleteMaterial: (id: string) => invoke<void>("delete_material", { id }),
  // Artifacts
  listArtifacts: (opts?: { subjectId?: string; kind?: ArtifactKind }) =>
    invoke<Artifact[]>("list_artifacts", { subjectId: opts?.subjectId ?? null, kind: opts?.kind ?? null }),
  createArtifact: (input: NewArtifact) => invoke<Artifact>("create_artifact", { input }),
  deleteArtifact: (id: string) => invoke<void>("delete_artifact", { id }),
  // Study sessions
  addSession: (input: NewSession) => invoke<StudySession>("add_session", { input }),
  listSessions: (opts?: { subjectId?: string; limit?: number }) =>
    invoke<StudySession[]>("list_sessions", { subjectId: opts?.subjectId ?? null, limit: opts?.limit ?? 500 }),
  deleteSession: (id: string) => invoke<void>("delete_session", { id }),
  /** Persist a photographed page's image bytes on-device → returns its path (OCR input only). */
  savePagePhoto: (bytes: number[], ext: string) => invoke<string>("save_page_photo", { bytes, ext }),
  // The photographed pages behind a material — only the on-device path is stored.
  readPhoto: (name: string) => invoke<number[]>("read_photo", { name }),
  updateArtifactBody: (id: string, body: string) => invoke<void>("update_artifact_body", { id, body }),
  addMaterialPhoto: (materialId: string, path: string) => invoke<MaterialPhoto>("add_material_photo", { materialId, path }),
  listMaterialPhotos: (materialId: string) => invoke<MaterialPhoto[]>("list_material_photos", { materialId }),
  deleteMaterialPhoto: (id: string) => invoke<void>("delete_material_photo", { id }),
  /** On-device OCR (Apple Vision) of a saved page photo → recognized text. */
  ocrImage: (path: string) => invoke<string>("ocr_image", { path }),
};

// ── Pure policy / display helpers ──────────────────────────────────────────────

export const KIND_LABELS: Record<ArtifactKind, string> = {
  notes: "Study notes",
  explanation: "Explanation",
  plan: "Plan",
};

export const SOURCE_LABELS: Record<MaterialSource, string> = {
  typed: "Pasted",
  photo: "Photo",
  lecture: "Lecture",
};

/** Current-understanding levels for Explain (drives the brain's layering emphasis). */
export const LEVELS = [
  { id: "new", label: "New to this" },
  { id: "shaky", label: "Seen it, still shaky" },
  { id: "revising", label: "Revising for a test" },
] as const;
export type Level = (typeof LEVELS)[number]["id"];

/** Normalize a topic tag — lowercase, trim, collapse internal whitespace. */
export function normalizeTag(raw: string): string {
  return raw.trim().toLowerCase().split(/\s+/).filter(Boolean).join(" ");
}

/** Dedupe + preserve first-occurrence order; trims blanks. */
export function dedupeStrings(raw: Iterable<string>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const r of raw) {
    const t = r.trim();
    if (!t) continue;
    const key = normalizeTag(t);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

export const formatLocalDate = (d: Date): string =>
  `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
export const todayLocal = (): string => formatLocalDate(new Date());

/** Human "x days ago" for a YYYY-MM-DD date, relative to local today. */
export function relativeDays(dateStr: string | null): string {
  if (!dateStr) return "never";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  if (!m) return "never";
  const then = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const now = new Date();
  const days = Math.round((new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() - then.getTime()) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.round(days / 7)} wk ago`;
  if (days < 365) return `${Math.round(days / 30)} mo ago`;
  return `${Math.round(days / 365)} yr ago`;
}

/** Human minutes → "1h 20m". */
export function formatMinutes(min: number): string {
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}
